import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { getMarketplaceFromEbay, getCurrencyCode, getCurrencySymbol } from '../utils/currency';
import { api } from '../services/api';

// ============================================================================
// Types
// ============================================================================

export interface Store {
  id: number;
  account_name: string;
  ebay_username: string | null;
  ebay_user_id: string | null;
  marketplace_id: string;
  store_logo_url: string | null;
  is_default: boolean;
  is_active: boolean;
  connection_status: string;
  connected_at: string | null;
  last_verified_at: string | null;
  // Stats
  products_count: number;
  orders_count: number;
  managed_count: number;
  oos_count?: number;
}

export interface StoreLimits {
  max_stores: number;
  current_stores: number;
  stores_remaining: number;
  can_add_store: boolean;
  subscription_plan: string;
}

export interface StoreStats {
  total: {
    products: number;
    managed: number;
    orders_this_month: number;
    listings: number;
  };
  stores: Store[];
}

interface StoreContextType {
  stores: Store[];
  selectedStore: Store | null;
  loading: boolean;
  error: string | null;
  limits: StoreLimits | null;
  stats: StoreStats | null;

  // Marketplace helpers (derived from selectedStore)
  marketplace: string;     // "GB", "US", "DE", etc. (default: "GB")
  currencyCode: string;    // "GBP", "USD", "EUR", etc. (default: "GBP")
  currencySymbol: string;  // "£", "$", "€", etc. (default: "£")

  // Actions
  fetchStores: () => Promise<void>;
  selectStore: (storeId: number) => void;
  setDefaultStore: (storeId: number) => Promise<void>;
  updateStore: (storeId: number, data: Partial<Store>) => Promise<void>;
  deleteStore: (storeId: number) => Promise<any>; // [EBAY-BAGLANTI] sunucu cevabi (ebay_path) ekrana tasinir
  fetchLimits: () => Promise<void>;
  fetchStats: () => Promise<void>;
}

const StoreContext = createContext<StoreContextType>({
  stores: [],
  selectedStore: null,
  loading: false,
  error: null,
  limits: null,
  stats: null,
  marketplace: 'GB',
  currencyCode: 'GBP',
  currencySymbol: '£',
  fetchStores: async () => {},
  selectStore: () => {},
  setDefaultStore: async () => {},
  updateStore: async () => {},
  deleteStore: async () => {},
  fetchLimits: async () => {},
  fetchStats: async () => {},
});

export const useStore = () => useContext(StoreContext);

// ============================================================================
// Provider
// ============================================================================

interface StoreProviderProps {
  children: ReactNode;
}

// Get initial store from localStorage for INSTANT loading (no API wait)
const getInitialStore = (): Store | null => {
  // Safety check for SSR or environments without localStorage
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const savedStore = localStorage.getItem('selectedStoreData');
    if (savedStore) {
      const parsed = JSON.parse(savedStore);
      // Validate that parsed data has required fields
      if (parsed && typeof parsed.id === 'number') {
        console.log('[StoreContext] INSTANT load from localStorage:', parsed?.ebay_username);
        return parsed as Store;
      }
    }
  } catch (e) {
    console.error('[StoreContext] Failed to parse stored store data:', e);
    // Clear corrupted data
    try {
      localStorage.removeItem('selectedStoreData');
    } catch {}
  }
  return null;
};

export const StoreProvider: React.FC<StoreProviderProps> = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [stores, setStores] = useState<Store[]>([]);
  // INSTANT: Load from localStorage immediately (no API wait)
  const [selectedStore, setSelectedStore] = useState<Store | null>(getInitialStore());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limits, setLimits] = useState<StoreLimits | null>(null);
  const [stats, setStats] = useState<StoreStats | null>(null);

  // Fetch all stores
  const fetchStores = useCallback(async () => {
    console.log('[StoreContext] fetchStores called, token:', token ? 'exists' : 'missing');
    if (!token) return;

    try {
      setLoading(true);
      setError(null);

      console.log('[StoreContext] Fetching stores via api.get');
      const data: Store[] = await api.get<Store[]>('/stores/');
      console.log('[StoreContext] Fetched stores:', data);
      setStores(data);

      // Restore selected store from localStorage or use default
      const savedStoreId = localStorage.getItem('selectedStoreId');
      let storeToSelect: Store | null = null;

      if (savedStoreId) {
        storeToSelect = data.find(s => s.id === parseInt(savedStoreId)) || null;
      }

      if (!storeToSelect) {
        // Use default store or first store
        storeToSelect = data.find(s => s.is_default) || data[0] || null;
      }

      if (storeToSelect) {
        setSelectedStore(storeToSelect);
        localStorage.setItem('selectedStoreId', storeToSelect.id.toString());
        localStorage.setItem('selectedStoreData', JSON.stringify(storeToSelect));
      }

    } catch (err: any) {
      console.error('Failed to fetch stores:', err);
      setError(err.message || 'Failed to fetch stores');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Select a store
  const selectStore = useCallback((storeId: number) => {
    const store = stores.find(s => s.id === storeId);
    if (store) {
      setSelectedStore(store);
      localStorage.setItem('selectedStoreId', storeId.toString());
      localStorage.setItem('selectedStoreData', JSON.stringify(store));
    }
  }, [stores]);

  // Set default store
  const setDefaultStore = useCallback(async (storeId: number) => {
    if (!token) return;

    try {
      await api.put(`/stores/${storeId}/set-default`);
      await fetchStores();
    } catch (err: any) {
      console.error('Failed to set default store:', err);
      throw err;
    }
  }, [token, fetchStores]);

  // Update store
  const updateStore = useCallback(async (storeId: number, data: Partial<Store>) => {
    if (!token) return;

    try {
      await api.put(`/stores/${storeId}`, data);

      // Refresh stores
      await fetchStores();
    } catch (err: any) {
      console.error('Failed to update store:', err);
      throw err;
    }
  }, [token, fetchStores]);

  // Delete store
  const deleteStore = useCallback(async (storeId: number) => {
    if (!token) return;

    try {
      const cevap = await api.delete(`/stores/${storeId}`);

      // If deleted store was selected, clear selection
      if (selectedStore?.id === storeId) {
        setSelectedStore(null);
        localStorage.removeItem('selectedStoreId');
      }

      // Refresh stores
      await fetchStores();
      return cevap;
    } catch (err: any) {
      console.error('Failed to delete store:', err);
      throw err;
    }
  }, [token, fetchStores, selectedStore]);

  // Fetch limits
  const fetchLimits = useCallback(async () => {
    if (!token) return;

    try {
      const data = await api.get<StoreLimits>('/stores/limits');
      setLimits(data);
    } catch (err: any) {
      console.error('Failed to fetch limits:', err);
    }
  }, [token]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!token) return;

    try {
      const data = await api.get<StoreStats>('/stores/stats/all');
      setStats(data);
    } catch (err: any) {
      console.error('Failed to fetch stats:', err);
    }
  }, [token]);

  // Marketplace helpers: derived from selectedStore, always falls back to GB
  const marketplace = useMemo(() => {
    if (!selectedStore?.marketplace_id) return 'GB';
    return getMarketplaceFromEbay(selectedStore.marketplace_id);
  }, [selectedStore?.marketplace_id]);

  const currencyCode = useMemo(() => getCurrencyCode(marketplace), [marketplace]);
  const currencySymbol = useMemo(() => getCurrencySymbol(marketplace), [marketplace]);

  // Track if user explicitly logged out vs initial load
  const wasAuthenticatedRef = useRef(false);

  // Initial fetch on authentication
  useEffect(() => {
    console.log('[StoreContext] Auth state changed:', { isAuthenticated, hasToken: !!token });
    if (isAuthenticated && token) {
      wasAuthenticatedRef.current = true;
      console.log('[StoreContext] Fetching stores and limits...');
      fetchStores();
      fetchLimits();
    } else if (wasAuthenticatedRef.current) {
      // Only clear on EXPLICIT logout (was authenticated before, now not)
      console.log('[StoreContext] Clearing state (user logged out)');
      wasAuthenticatedRef.current = false;
      setStores([]);
      setSelectedStore(null);
      setLimits(null);
      setStats(null);
      localStorage.removeItem('selectedStoreData');
    } else {
      // Initial load with false auth - keep cached data, don't clear
      console.log('[StoreContext] Initial load - keeping cached selectedStore');
    }
  }, [isAuthenticated, token, fetchStores, fetchLimits]);

  const value = {
    stores,
    selectedStore,
    loading,
    error,
    limits,
    stats,
    marketplace,
    currencyCode,
    currencySymbol,
    fetchStores,
    selectStore,
    setDefaultStore,
    updateStore,
    deleteStore,
    fetchLimits,
    fetchStats,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

export default StoreContext;
